// src/components/views/AlertsView.tsx
import React, { useMemo, useState } from 'react';
import { Siren, Building2, Link, Gem, Scale, CircleCheck } from 'lucide-react';
import { useSimulationStore, AlertData } from '../../store/useSimulationStore';
import StatusBadge from '../common/StatusBadge';
import SearchSortControls, { SortOption } from '../common/SearchSortControls';
import KpiCard from '../common/KpiCard';
import Dot from '../common/StatusDot';

// ─── Severity helpers ─────────────────────────────────────────────────────────
const severityIcon = (s: string): React.ReactNode =>
   s === 'critical' ? <Dot color="#f87171" /> : s === 'warning' ? <Dot color="#facc15" /> : <Dot color="#60a5fa" />;

const severityBorder = (s: string) =>
   s === 'critical' ? 'border-red-700' : s === 'warning' ? 'border-yellow-700' : 'border-blue-700';

const severityBg = (s: string) =>
   s === 'critical' ? 'bg-red-950/40' : s === 'warning' ? 'bg-yellow-950/30' : 'bg-blue-950/30';

const severityLabel = (s: string) =>
   s === 'critical' ? 'text-red-400' : s === 'warning' ? 'text-yellow-400' : 'text-blue-400';

const categoryIcon = (c: string): React.ReactNode =>
   c === 'node' ? <Building2 className="w-3.5 h-3.5" />
      : c === 'connection' ? <Link className="w-3.5 h-3.5" />
      : c === 'resource' ? <Gem className="w-3.5 h-3.5" />
      : <Scale className="w-3.5 h-3.5" />;

type FilterCategory = 'all' | 'node' | 'connection' | 'resource' | 'policy';
type FilterSeverity = 'all' | 'critical' | 'warning' | 'info';
type AlertSortKey  = 'severity' | 'tick' | 'category';

// ─── Sort options ─────────────────────────────────────────────────────────────
const SORT_OPTIONS: SortOption<AlertSortKey>[] = [
   { key: 'severity', label: 'Severity' },
   { key: 'tick',     label: 'Tick'     },
   { key: 'category', label: 'Category' },
];

// ─── AlertsView ──────────────────────────────────────────────────────────────
const AlertsView: React.FC = () => {
   const alerts                   = useSimulationStore(s => s.alerts);
   const tick                     = useSimulationStore(s => s.tick);
   const setSelectedNodeId        = useSimulationStore(s => s.setSelectedNodeId);
   const setSelectedConnectionId  = useSimulationStore(s => s.setSelectedConnectionId);
   const setSelectedResourceNodeId = useSimulationStore(s => s.setSelectedResourceNodeId);
   
   const [search,         setSearch]         = useState('');
   const [filterCategory, setFilterCategory] = useState<FilterCategory>('all');
   const [filterSeverity, setFilterSeverity] = useState<FilterSeverity>('all');
   const [sortKey,        setSortKey]        = useState<AlertSortKey>('severity');
   const [sortAsc,        setSortAsc]        = useState(true);
   
   // ─── Counts for KPI row ───────────────────────────────────────────────────
   const counts = useMemo(() => ({
      total:      alerts.length,
      critical:   alerts.filter(a => a.severity === 'critical').length,
      warning:    alerts.filter(a => a.severity === 'warning').length,
      info:       alerts.filter(a => a.severity === 'info').length,
      node:       alerts.filter(a => a.category === 'node').length,
      connection: alerts.filter(a => a.category === 'connection').length,
      resource:   alerts.filter(a => a.category === 'resource').length,
      policy:     alerts.filter(a => a.category === 'policy').length,
   }), [alerts]);
   
   // ─── Filtered + sorted list ───────────────────────────────────────────────
   const filtered = useMemo(() => {
      let result = [...alerts];
      if (filterCategory !== 'all') result = result.filter(a => a.category === filterCategory);
      if (filterSeverity !== 'all') result = result.filter(a => a.severity === filterSeverity);
      if (search.trim()) {
         const q = search.toLowerCase();
         result = result.filter(a =>
            a.title.toLowerCase().includes(q) ||
            a.message.toLowerCase().includes(q) ||
            a.entity_name?.toLowerCase().includes(q)
         );
      }
      
      // Sort: critical first, then warning, then info (default)
      const sevOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
      result.sort((a, b) => {
         let cmp = 0;
         if (sortKey === 'severity') cmp = (sevOrder[a.severity] ?? 3) - (sevOrder[b.severity] ?? 3);
         if (sortKey === 'tick')     cmp = (a.tick ?? 0) - (b.tick ?? 0);
         if (sortKey === 'category') cmp = a.category.localeCompare(b.category);
         return sortAsc ? cmp : -cmp;
      });
      return result;
   }, [alerts, filterCategory, filterSeverity, search, sortKey, sortAsc]);
   
   // ─── Sort toggle handler ──────────────────────────────────────────────────
   const handleSortChange = (key: AlertSortKey) => {
      if (key === sortKey) setSortAsc(a => !a);
      else { setSortKey(key); setSortAsc(true); }
   };
   
   // ─── Alert click → open detail modal ─────────────────────────────────────
   const handleAlertClick = (alert: AlertData) => {
      if (alert.category === 'node'       && typeof alert.entity_id === 'number') setSelectedNodeId(alert.entity_id);
      else if (alert.category === 'connection' && typeof alert.entity_id === 'number') setSelectedConnectionId(alert.entity_id);
      else if (alert.category === 'resource'   && typeof alert.entity_id === 'number') setSelectedResourceNodeId(alert.entity_id);
   };
   
   return (
      <div className="space-y-3">
         
         {/* KPI row */}
         <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={<Siren className="w-4 h-4" />} label="Alerts" value={counts.total} />
            <KpiCard icon={<Dot color="#f87171" />} label="Critical" value={counts.critical} color={counts.critical > 0 ? 'text-red-400'    : 'text-gray-500'} />
            <KpiCard icon={<Dot color="#facc15" />} label="Warning"  value={counts.warning}  color={counts.warning  > 0 ? 'text-yellow-400' : 'text-gray-500'} />
            <KpiCard icon={<Dot color="#60a5fa" />} label="Info"     value={counts.info}     color={counts.info     > 0 ? 'text-blue-400'   : 'text-gray-500'} />
         </div>
         
         {/* Filters + search via SearchSortControls */}
         <SearchSortControls
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search alerts…"
            sortKey={sortKey}
            sortAsc={sortAsc}
            onSortChange={handleSortChange}
            sortOptions={SORT_OPTIONS}
            totalCount={alerts.length}
            filteredCount={filtered.length}
         >
            {/* Category filter */}
            <div className="flex items-center gap-1 flex-wrap">
               {(['all', 'node', 'connection', 'resource', 'policy'] as const).map((cat) => (
                  <StatusBadge
                     key={cat}
                     onClick={() => setFilterCategory(cat)}
                     active={filterCategory === cat}
                     className={filterCategory === cat
                        ? 'bg-blue-900/50 border-blue-700 text-blue-300'
                        : 'bg-gray-800 border-gray-700 text-gray-400'}
                  >
                     {{
                        all: 'All' as React.ReactNode,
                        node: <Building2 className="w-3.5 h-3.5" />,
                        connection: <Link className="w-3.5 h-3.5" />,
                        resource: <Gem className="w-3.5 h-3.5" />,
                        policy: <Scale className="w-3.5 h-3.5" />,
                     }[cat]}
                     <span className="hidden sm:inline">
                        {{all: '', node: ' Nodes', connection: ' Conns', resource: ' Res', policy: ' Policy'}[cat]}
                     </span>
                  </StatusBadge>
               ))}
            </div>
            
            {/* Severity filter */}
            <div className="flex items-center gap-1 flex-wrap">
               {(['all', 'critical', 'warning', 'info'] as const).map((sev) => (
                  <StatusBadge
                     key={sev}
                     onClick={() => setFilterSeverity(sev)}
                     active={filterSeverity === sev}
                     className={filterSeverity === sev
                        ? 'bg-blue-900/50 border-blue-700 text-blue-300'
                        : 'bg-gray-800 border-gray-700 text-gray-400'}
                  >
                     {{
                        all: 'All' as React.ReactNode,
                        critical: <Dot color="#f87171" />,
                        warning: <Dot color="#facc15" />,
                        info: <Dot color="#60a5fa" />,
                     }[sev]}
                     <span className="hidden sm:inline">
                        {{all: '', critical: ' Crit', warning: ' Warn', info: ' Info'}[sev]}
                     </span>
                  </StatusBadge>
               ))}
            </div>
         </SearchSortControls>
         
         {/* Alerts list */}
         <div className="flex flex-col gap-2">
            {filtered.length === 0 ? (
               <div className="text-center text-gray-500 py-12">
                  <div className="flex justify-center mb-2"><CircleCheck className="w-9 h-9 text-green-500" /></div>
                  <div>No alerts — all systems nominal.</div>
                  <div className="text-xs mt-1">Tick #{tick}</div>
               </div>
            ) : (
               filtered.map((alert) => (
                  <button key={alert.id} onClick={() => handleAlertClick(alert)}
                          className={`w-full text-left ${severityBg(alert.severity)} border ${severityBorder(alert.severity)} rounded-xl p-4 hover:brightness-125 transition-all`}>
                     <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                           <span>{severityIcon(alert.severity)}</span>
                           <span className="font-semibold text-white text-sm">{alert.title}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                           <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${severityLabel(alert.severity)} bg-gray-900/50`}>{alert.severity}</span>
                           <span className="text-xs text-gray-500 bg-gray-900/50 px-1.5 py-0.5 rounded">{alert.category}</span>
                        </div>
                     </div>
                     <div className="text-xs text-gray-400 mt-1">{categoryIcon(alert.category)} {alert.entity_name}</div>
                     <div className="text-sm text-gray-300 mt-1">{alert.message}</div>
                     <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>Tick #{alert.tick}</span>
                        {alert.value != null && (
                           <span>val: {typeof alert.value === 'number' ? alert.value.toFixed(2) : alert.value}</span>
                        )}
                     </div>
                  </button>
               ))
            )}
         </div>
         
         {/* Footer hint */}
         <div className="text-xs text-gray-600 text-center pb-2">
            Alerts refresh every heartbeat · Showing {filtered.length} of {alerts.length} alerts · Tick #{tick}
         </div>
      
      </div>
   );
};

export default AlertsView;
